import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { products, Product } from '@/lib/data'
import { ProductCard } from '@/components/ProductCard'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { gtmEvents } from '@/lib/gtm'

const Shop = () => {
  const [searchParams] = useSearchParams()
  const categoryParam = searchParams.get('category')
  const queryParam = searchParams.get('q')

  const [category, setCategory] = useState<string>(categoryParam || 'all')
  const [priceRange, setPriceRange] = useState([0, 200])
  const [sort, setSort] = useState('popular')
  const [searchTerm, setSearchTerm] = useState(queryParam || '')

  useEffect(() => {
    if (categoryParam) setCategory(categoryParam)
  }, [categoryParam])

  useEffect(() => {
    if (queryParam) setSearchTerm(queryParam)
  }, [queryParam])

  const filteredProducts = useMemo(() => {
    let result = products

    if (category !== 'all') {
      result = result.filter((p) => p.category === category)
    }

    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1],
    )

    if (searchTerm) {
      const lower = searchTerm.toLowerCase()
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(lower) ||
          p.description.toLowerCase().includes(lower) ||
          p.tags.some((t) => t.toLowerCase().includes(lower)),
      )
    }

    if (sort === 'price_asc') {
      result = [...result].sort((a, b) => a.price - b.price)
    } else if (sort === 'price_desc') {
      result = [...result].sort((a, b) => b.price - a.price)
    } else {
      result = [...result].sort((a, b) => b.reviews - a.reviews)
    }

    return result
  }, [category, priceRange, sort, searchTerm])

  useEffect(() => {
    gtmEvents.viewItemList(
      filteredProducts.map((p) => ({
        item_id: p.id,
        item_name: p.name,
        price: p.price,
        item_category: p.category,
      })),
    )
  }, [filteredProducts])

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Loja</h1>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Filters Sidebar */}
        <aside className="w-full md:w-64 space-y-8">
          <div>
            <h3 className="font-semibold mb-3">Buscar</h3>
            <Input
              placeholder="Nome do produto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <h3 className="font-semibold mb-3">Categoria</h3>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="Moletons">Moletons</SelectItem>
                <SelectItem value="Camisetas">Camisetas</SelectItem>
                <SelectItem value="Canecas">Canecas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <h3 className="font-semibold mb-3">
              Preço: R$ {priceRange[0]} - R$ {priceRange[1]}
            </h3>
            <Slider
              defaultValue={[0, 200]}
              max={200}
              step={10}
              value={priceRange}
              onValueChange={setPriceRange}
              className="py-4"
            />
          </div>

          <div>
            <h3 className="font-semibold mb-3">Ordenar por</h3>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Mais Populares</SelectItem>
                <SelectItem value="price_asc">Menor Preço</SelectItem>
                <SelectItem value="price_desc">Maior Preço</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        {/* Product Grid */}
        <div className="flex-1">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Nenhum produto encontrado com esses filtros.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Shop
